import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, HelpCircle, Settings, User, Video } from 'lucide-react';

export const featureExplainer: Playbook = {
  meta: {
    id: 'feature-explainer',
    slug: 'feature-explainer',
    categoryId: 'onboard-educate',
    name: 'The In-Context Feature Explainer',
    tagline: 'Explain complex features, right where users need it.',
    description: 'On pages with complex UI, embed a Genie that can answer questions and provide step-by-step walkthroughs of specific features.',
    strategy: 'On pages with complex UI, embed a Genie that can answer common questions or provide a step-by-step walkthrough of that specific feature.',
    benefits: [
      'Contextual help where needed',
      'Reduced support tickets',
      'Improved feature adoption',
      'Better user experience'
    ],
    targetAudience: ['SaaS platforms', 'Analytics tools', 'Complex software'],
    estimatedSetupTime: '25 minutes',
    difficulty: 'intermediate',
    tags: ['feature-explanation', 'contextual-help', 'ui-assistance', 'product-education'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Feature Explainer Playbook - In-Context Genie Help',
    metaDescription: 'Provide contextual Genie assistance for complex features. Reduce support burden and improve feature adoption.',
    keywords: ['feature explanation', 'contextual help', 'ui assistance', 'product education', 'feature adoption']
  },
  demoConfig: {
    triggerType: 'button',
    message: "I see you're exploring our analytics dashboard. This can be powerful but might seem complex at first. Would you like me to explain what each metric means, or walk you through creating your first report?",
    position: 'bottom-right'
  },
  demoScript: [
    { speaker: 'agent', message: "I can help explain this dashboard. What would you like to know?" },
    { speaker: 'user', message: "What does conversion rate mean here?" },
    { speaker: 'agent', message: "Conversion rate shows the percentage of visitors who completed your goal action." },
    { speaker: 'user', message: "How do I improve it?" },
    { speaker: 'agent', message: "Try A/B testing your CTAs. I can show you how to set that up!" }
  ],
  templateConfig: {
    firstMessage: "Need help with this feature? I can explain how it works or walk you through it step-by-step.",
    systemPrompt: "You are a feature-expert Genie. Help users understand and effectively use specific features. Provide clear, step-by-step guidance and practical examples.",
    tools: ['screen_sharing', 'tutorial_videos']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'User encounters complex feature',
      description: 'User navigates to advanced feature page or complex interface section'
    },
    {
      type: 'genie',
      icon: HelpCircle,
      genieIcon: 'genie-lamp',
      label: 'Offers contextual help',
      description: 'Genie detects user on complex page and proactively offers assistance'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Explains feature purpose',
      description: 'Clarifies what the feature does and why it\'s valuable for user\'s goals'
    },
    {
      type: 'action',
      icon: Settings,
      genieIcon: 'settings',
      label: 'Provides step-by-step guidance',
      description: 'Walks through feature usage with clear, actionable instructions'
    },
    {
      type: 'action',
      icon: Video,
      genieIcon: 'share',
      label: 'Offers additional resources',
      description: 'Links to tutorials, documentation, or advanced training materials'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Feature mastery achieved',
      description: 'User successfully understands and uses complex features effectively'
    }
  ],
  metrics: {
    conversionRate: '64%',
    responseTime: '<1 second',
    satisfactionScore: '4.6/5',
    costSavings: '$45k/month',
    timeToValue: '3 minutes'
  },
  testimonials: [
    {
      companyName: 'DataInsights Pro',
      authorName: 'Lisa Wang',
      authorTitle: 'UX Director',
      quote: 'Feature adoption increased 40% after adding contextual explainers. Users are much more confident using advanced features.',
      metrics: '40% increase in feature adoption'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Contextual Help Implementation',
      description: 'How to identify and implement help points for complex features',
      url: '/docs/contextual-help-setup'
    },
    {
      type: 'template',
      title: 'Feature Explanation Scripts',
      description: 'Templates for explaining different types of software features',
      url: '/templates/feature-explanations'
    }
  ]
};